import React, { useEffect } from 'react';
import type { BrannanWebsiteHeaderSettings } from '@shared/types/WebsiteHeaderSettings.types';
import { EventNameNavProps } from './components/EventNameNav';
import { isInIframe } from '@shared/utils/isInIframe';
import { sendMessageToParentWindow } from '@shared/utils/previewMessageBus';
import {
  CenterLayout,
  FullWidthLayout,
  NoPhotoLayout,
  PageWidthLayout,
  PhotoArchLayout,
  PhotoDuoLeftLayout,
  PhotoDuoRightLayout,
  PhotoMarqueeLayout,
  PhotoSliderLayout,
  PhotoTrioLayout,
  MonogramLayout,
  PhotoTrioPageWidthLayout,
  VideoArchLayout,
} from './layouts';

export interface HeaderV2Props extends EventNameNavProps {
  websiteHeader: NonNullable<BrannanWebsiteHeaderSettings['websiteHeader']>;
  pageId?: string;
}

const isPreview = isInIframe();

/**
 * This set of header layouts utilizes the new `websiteHeader` object.
 *
 * Each layout will render:
 * - Title
 * - Nav
 * - Header Photo
 */
export const HeaderV2 = (props: HeaderV2Props) => {
  const { websiteHeader, ...restProps } = props;

  const headerPhoto = websiteHeader.headerPhoto;
  const showPlaceholder = isPreview && !!headerPhoto.presentationLayout;

  useEffect(() => {
    if (headerPhoto.presentationLayout) {
      sendMessageToParentWindow({ action: 'requestIframeScaleValue', source: 'joyPreview' });
    }
  }, [headerPhoto.presentationLayout]);

  switch (headerPhoto.presentationLayout) {
    // have to rebuild websiteHeader explicitly because despite the switch,
    // TSC complains about the discriminated union whose shared type is 2 levels deep

    case 'center':
      return (
        <CenterLayout
          websiteHeader={{ ...websiteHeader, headerPhoto }}
          showPlaceholder={showPlaceholder}
          {...restProps}
        />
      );
    case 'fullWidth':
      return (
        <FullWidthLayout
          websiteHeader={{ ...websiteHeader, headerPhoto }}
          showPlaceholder={showPlaceholder}
          {...restProps}
        />
      );
    case 'pageWidth':
      return (
        <PageWidthLayout
          websiteHeader={{ ...websiteHeader, headerPhoto }}
          showPlaceholder={showPlaceholder}
          {...restProps}
        />
      );

    case 'photoSlider':
      return (
        <PhotoSliderLayout
          websiteHeader={{ ...websiteHeader, headerPhoto }}
          showPlaceholder={showPlaceholder}
          {...restProps}
        />
      );

    case 'photoTrio':
      return (
        <PhotoTrioLayout
          websiteHeader={{ ...websiteHeader, headerPhoto }}
          showPlaceholder={showPlaceholder}
          {...restProps}
        />
      );

    case 'photoTrioPageWidth':
      return (
        <PhotoTrioPageWidthLayout
          websiteHeader={{ ...websiteHeader, headerPhoto }}
          showPlaceholder={showPlaceholder}
          {...restProps}
        />
      );

    case 'noPhoto':
      return (
        <NoPhotoLayout
          websiteHeader={{ ...websiteHeader, headerPhoto }}
          showPlaceholder={showPlaceholder}
          {...restProps}
        />
      );

    case 'monogram':
      return (
        <MonogramLayout
          websiteHeader={{ ...websiteHeader, headerPhoto }}
          showPlaceholder={showPlaceholder}
          {...restProps}
        />
      );

    case 'photoArch':
      return (
        <PhotoArchLayout
          websiteHeader={{ ...websiteHeader, headerPhoto }}
          showPlaceholder={showPlaceholder}
          {...restProps}
        />
      );

    case 'photoDuoLeft':
      return (
        <PhotoDuoLeftLayout
          websiteHeader={{ ...websiteHeader, headerPhoto }}
          showPlaceholder={showPlaceholder}
          {...restProps}
        />
      );

    case 'photoDuoRight':
      return (
        <PhotoDuoRightLayout
          websiteHeader={{ ...websiteHeader, headerPhoto }}
          showPlaceholder={showPlaceholder}
          {...restProps}
        />
      );

    case 'photoMarquee':
      return (
        <PhotoMarqueeLayout
          websiteHeader={{ ...websiteHeader, headerPhoto }}
          showPlaceholder={showPlaceholder}
          {...restProps}
        />
      );

    case 'videoArch':
      return (
        <VideoArchLayout
          websiteHeader={{ ...websiteHeader, headerPhoto }}
          showPlaceholder={showPlaceholder}
          {...restProps}
        />
      );

    default:
      return null;
  }
};

export default HeaderV2;
